import React from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #07090e;
  --panel: #0d111a;
  --surface: #131824;
  --text: #f3f4f8;
  --dim: #9aa0b2;
  --faint: #636878;
  --accent: #0ea5e9;
  --accent-glow: rgba(14, 165, 233, 0.15);
  --success: #10b981;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.68;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 780px;
  margin: 0 auto;
  padding: 44px 22px 90px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 24px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: linear-gradient(145deg, #0284c7, #38bdf8);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: 26px;
  box-shadow: 0 8px 24px rgba(14, 165, 233, 0.35);
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -1px;
  margin: 8px 0 6px;
  color: #ffffff;
`;

const Updated = styled.p`
  color: var(--faint);
  font-size: 14px;
  margin-bottom: 32px;
`;

const Section = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 38px 0 12px;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
    flex: none;
  }
`;

const Body = styled.div`
  p,
  li {
    color: var(--dim);
    font-size: 15.5px;
    line-height: 1.7;
    margin-bottom: 12px;
  }

  ul {
    padding-left: 20px;
    margin-bottom: 18px;
  }

  li {
    margin-bottom: 6px;
  }

  strong {
    color: var(--text);
  }

  a {
    color: var(--accent);
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Card = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 22px 24px;
  margin: 18px 0 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
`;

const HighlightBox = styled(Card)`
  background: linear-gradient(145deg, rgba(14, 165, 233, 0.08), rgba(99, 102, 241, 0.05));
  border: 1px solid rgba(56, 189, 248, 0.25);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin: 18px 0;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureBox = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px 18px;

  h4 {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: var(--dim);
  }
`;

const Footer = styled.footer`
  margin-top: 60px;
  padding-top: 24px;
  border-top: 1px solid var(--border);
  color: var(--faint);
  font-size: 13.5px;
  text-align: center;
`;

const Qapture = () => {
  return (
    <Page>
      <Wrap>
        <Header>
          <Badge>📡</Badge>
          <div>
            <Brand>
              ScanFi / <span>Qapture</span>
            </Brand>
            <span style={{ fontSize: "13px", color: "var(--faint)" }}>
              Wi-Fi Management, Smart QR &amp; Barcode Studio
            </span>
          </div>
        </Header>

        <Title>Privacy Policy</Title>
        <Updated>Last updated: September 25, 2026</Updated>

        <Body>
          <HighlightBox>
            <p style={{ margin: 0, color: "#e0f2fe", fontSize: "16px" }}>
              <strong>Summary &amp; Privacy Guarantee:</strong> ScanFi / Qapture (Package:{" "}
              <strong>com.anonymous.Qapture</strong>) is built upon a strict{" "}
              <strong>100% on-device, privacy-first architecture</strong>. All Wi-Fi network credentials,
              camera optical scans, barcodes, generated QR codes, and household device lists remain entirely
              on your physical hardware. We do not track, collect, upload, share, or sell your personal data.
            </p>
          </HighlightBox>

          <Section>1. Core Architecture: 100% Local &amp; Private</Section>
          <p>
            ScanFi / Qapture serves as your home network toolkit, barcode reader, and QR code creator.
            Every feature is executed completely client-side:
          </p>
          <ul>
            <li>
              <strong>No Cloud Storage:</strong> Your Wi-Fi network names (SSIDs), passwords, encrypted QR payloads,
              and scan history are saved exclusively within your device&apos;s sandboxed local storage (<code>AsyncStorage</code>).
            </li>
            <li>
              <strong>No Accounts or Registration:</strong> You are not required to create an account, sign in, or provide an
              email address, name, or phone number to use any capability within the app.
            </li>
            <li>
              <strong>Local Real-Time Decoding:</strong> The camera barcode decoder processes optical frames instantaneously in device
              memory. No pictures, videos, or image streams are ever saved to disk or transmitted to remote servers.
            </li>
            <li>
              <strong>Offline Operation:</strong> QR generation, card printing, history vault, and Wi-Fi code creation work seamlessly
              without an active internet connection.
            </li>
          </ul>

          <Section>2. Device Permissions &amp; Why We Need Them</Section>
          <p>
            The application requests strictly the minimal permissions necessary to enable user-triggered network management
            and scanning features:
          </p>

          <Grid>
            <FeatureBox>
              <h4>📷 Camera Permission</h4>
              <p>
                Used exclusively when you navigate to the Scan tab to scan Wi-Fi QR codes, product barcodes, URLs, and text notes.
                Optical frames are analyzed locally in real time and are never stored or uploaded.
              </p>
            </FeatureBox>

            <FeatureBox>
              <h4>📍 Location Permission (Wi-Fi SSID)</h4>
              <p>
                Required by the Android Operating System (<code>ACCESS_FINE_LOCATION</code>) to read the SSID (network name) of your
                currently connected Wi-Fi router. We do not track your physical GPS coordinates or location history.
              </p>
            </FeatureBox>

            <FeatureBox>
              <h4>📶 Wi-Fi &amp; Network State</h4>
              <p>
                Used to determine connection status, detect local gateway IP addresses (such as 192.168.1.1), and calculate Wi-Fi channel
                frequencies (2.4GHz / 5GHz / 6GHz).
              </p>
            </FeatureBox>

            <FeatureBox>
              <h4>📳 Haptics &amp; Vibration</h4>
              <p>
                Provides subtle, tactile physical confirmation upon successful QR/barcode detection, copying credentials, or saving cards.
              </p>
            </FeatureBox>
          </Grid>

          <Section>3. Network Operations &amp; Router Gateway Access</Section>
          <p>
            ScanFi / Qapture includes tools to manage your home Wi-Fi and router settings:
          </p>
          <ul>
            <li>
              <strong>Router Admin Portal:</strong> When you tap &ldquo;Admin Portal&rdquo;, the app opens your local gateway address
              (e.g., <code>http://192.168.1.1</code> or <code>http://192.168.0.1</code>) directly in an in-app browser session. This connection
              is entirely between your phone and your physical router on your local private subnet.
            </li>
            <li>
              <strong>No External Transmission:</strong> Router passwords, admin credentials, and device lists configured in the app remain strictly
              local on your device and are never transmitted to any external server.
            </li>
          </ul>

          <Section>4. Data Retention, Backup &amp; Deletion</Section>
          <p>
            Because all data is stored locally in your device&apos;s sandboxed storage, you maintain full control:
          </p>
          <ul>
            <li>
              <strong>In-App Deletion:</strong> You can delete any saved Wi-Fi code, clear scan history items individually, or purge all stored data
              in one tap via <strong>Settings &rarr; Clear All Stored Data</strong>.
            </li>
            <li>
              <strong>Exporting Backups:</strong> You can export a full JSON backup of your saved networks and vault items directly from the Settings screen.
            </li>
            <li>
              <strong>Complete Removal:</strong> Uninstalling the application or clearing app storage in Android/iOS system settings permanently deletes
              all local databases, preferences, and cached cards.
            </li>
          </ul>

          <Section>5. Children&apos;s Privacy (COPPA &amp; GDPR)</Section>
          <p>
            ScanFi / Qapture is suitable for general audiences of all ages. We do not knowingly collect, store, or solicit personal information from children
            under the age of 13. Because the app functions without accounts or external data harvesting, it fully complies with COPPA, GDPR, and international
            child privacy frameworks.
          </p>

          <Section>6. Third-Party Services</Section>
          <p>
            The application does not include any third-party behavioral analytics, user telemetry trackers, or advertising SDKs. No personal or device data
            is shared with third parties.
          </p>

          <Section>7. Contact &amp; Support</Section>
          <Card>
            If you have questions, feedback, or inquiries regarding this Privacy Policy or the application, please contact:
            <br />
            <br />
            <strong>Developer / Maintainer:</strong> Zayn Miraj / Nimions
            <br />
            <strong>Email:</strong>{" "}
            <a href="mailto:hello@nimions.com">hello@nimions.com</a>
            <br />
            <strong>Data Deletion Information:</strong>{" "}
            <a href="/qapture/delete-account">/qapture/delete-account</a> or{" "}
            <a href="/scanfi/delete-account">/scanfi/delete-account</a>
          </Card>
        </Body>

        <Footer>
          &copy; 2026 ScanFi / Qapture. All rights reserved. This policy applies to the mobile application{" "}
          <strong>com.anonymous.Qapture</strong>.
        </Footer>
      </Wrap>
    </Page>
  );
};

export default Qapture;
